import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Formik, FormikErrors, FormikHandlers, FormikHelpers, FormikTouched } from 'formik';

import styles from '../AccountingSystemOpen.module.scss';
import { PaymentUpdate, PaymentInformation } from '../types';
import BankingUpdateMiniDialogue from '../BankingUpdateMiniDialogue/BankingUpdateMiniDialogue';
import { useBuildPaymentInfoRows, PaymentInfoRows } from './buildPaymentInfoRows.helper';
import { useClientData } from '../../../../../providers/ClientData.provider';
import useFetchRailzBillCreditNotes from '../../../../../hooks/useApi/useFetchRailzBillCreditNotes';
import useFetchRailzInvoiceCreditNotes from '../../../../../hooks/useApi/useFetchRailzInvoiceCreditNotes';

import {
  updateCustomerData,
  getAvailablePaymentMethodsDropdown,
  getRequiredIcon,
  getAvailablePaymentNew,
} from '../utils';
import useDataFetchIndicator from '../../../../../hooks/useDataFetchIndicator';
import { useTranslation } from 'react-i18next';

type AccountingSystemSingleDialogueProps = {
  isVisible: boolean;
  billsAndInvoices?: vp.TemporaryBillsOrInvoicesRails;
  financialInstitutions: {
    [key: string]: vp.FinancialInstitution;
  };
  onCollect: (savedValues: vp.TemporaryBillsOrInvoicesRails, isUpdated: boolean) => void;
  close: () => void;
  type: 'Payable' | 'Receivable';
  BusinessName: string;
  ServiceName: string;
};

type FC = FunctionComponent<AccountingSystemSingleDialogueProps>;
const {
  Modal,
  SubTitle,
  Box,
  Button,
  Select,
  DatepickerSimple,
  ElementContainer,
  TextField,
  FilterableMultiSelector,
  Icon,
} = vp;
const AccountingSystemSingleDialogue: FC = ({
  onCollect,
  isVisible,
  close,
  type,
  billsAndInvoices,
  financialInstitutions,
  BusinessName,
  ServiceName,
}: AccountingSystemSingleDialogueProps) => {
  const [updateBankVisible, setUpdateBankVisible] = useState(false);
  const [creditNotes, setCreditNotes] = useState<vp.CreditNote[]>([]);
  const [, , fetchRailzBillsCreditNotes] = useFetchRailzBillCreditNotes({});
  const [, , fetchRailzInvoiceCreditNotes] = useFetchRailzInvoiceCreditNotes({});
  const [billsAndInvoicesInfo, setBillsAndInvoicesInfo] = useState<vp.TemporaryBillsOrInvoicesRails | undefined>(
    billsAndInvoices,
  );
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [isUpdated, setIsUpdated] = useState(false);
  const {
    response: { Country },
  } = useClientData();
  const [defaultValues, setDefaultValues] = useState<PaymentUpdate>({
    PaymentMethod: billsAndInvoices?.paymentMethodUser || 'Bank Account',
    FinancialInstitutionNumber: billsAndInvoices?.Customer?.BankAccount?.FinancialInstitutionNumber,
    BankTransitNumber: billsAndInvoices?.Customer?.BankAccount?.BranchTransitNumber,
    AccountNumber: billsAndInvoices?.Customer?.BankAccount?.AccountNumber,
    Email: billsAndInvoices?.Customer?.Email || billsAndInvoices?.Customer?.Interac?.EmailAddress,
    Phone: billsAndInvoices?.Customer?.Phone || billsAndInvoices?.Customer?.Interac?.PhoneNumber || '',
    Question: billsAndInvoices?.Customer?.Interac?.Question,
    Answer: billsAndInvoices?.Customer?.Interac?.Answer,
    PhoneNumber: billsAndInvoices?.Customer?.BankAccount?.PhoneNumber,
    City: billsAndInvoices?.Customer?.BankAccount?.City,
    Address: billsAndInvoices?.Customer?.BankAccount?.Address,
    State: billsAndInvoices?.Customer?.BankAccount?.State,
    ZipCode: billsAndInvoices?.Customer?.BankAccount?.ZipCode,
    InteracMethod: billsAndInvoices?.Customer?.Interac?.EmailAddress
      ? 'Email Address'
      : billsAndInvoices?.Customer?.Interac?.PhoneNumber
        ? 'Phone Number'
        : 'Email Address',
    TokenDebit: billsAndInvoices?.Customer?.DebitCard?.Token,
    TokenCredit: billsAndInvoices?.Customer?.CreditCard?.Token,
    Language: billsAndInvoices?.Customer?.Interac?.Language || 'en',
  });
  const { t } = useTranslation('translations', { keyPrefix: 'AccountingSystem.OpenTab.Dialogs.SingleDialog' });
  const { t: tUtils } = useTranslation('translations', { keyPrefix: 'AccountingSystem.OpenTab.Utils' });

  useEffect(() => {
    const fetchData = async () => {
      if (isVisible) {
        setDataFetchingInfo(true, 'loading some data', 'fullScreen');
        if (type === 'Receivable') {
          if (billsAndInvoices?.customerRef?.id) {
            const creditsResponse = await fetchRailzInvoiceCreditNotes({
              BusinessName,
              ServiceName,
              CustomerRefID: billsAndInvoices?.customerRef?.id,
            });
            if (creditsResponse.Success && creditsResponse.CreditNotes) {
              setCreditNotes(creditsResponse.CreditNotes);
            } else {
              setCreditNotes([]);
            }
          }
        } else if (billsAndInvoices?.vendorRef?.id) {
          const creditsResponse = await fetchRailzBillsCreditNotes({
            BusinessName,
            ServiceName,
            VendorRefID: billsAndInvoices?.vendorRef?.id,
          });
          if (creditsResponse.Success && creditsResponse.CreditNotes) {
            setCreditNotes(creditsResponse.CreditNotes);
          } else {
            setCreditNotes([]);
          }
        }
        setDataFetchingInfo(false);
      } else {
        setCreditNotes([]);
      }
    };

    fetchData();
  }, [isVisible]);

  useEffect(() => {
    setBillsAndInvoicesInfo(billsAndInvoices);
  }, [billsAndInvoices]);
  useEffect(() => {
    setDefaultValues({
      PaymentMethod: billsAndInvoicesInfo?.paymentMethodUser || 'Bank Account',
      FinancialInstitutionNumber: billsAndInvoicesInfo?.Customer?.BankAccount?.FinancialInstitutionNumber,
      BankTransitNumber: billsAndInvoicesInfo?.Customer?.BankAccount?.BranchTransitNumber,
      AccountNumber: billsAndInvoicesInfo?.Customer?.BankAccount?.AccountNumber,
      Email: billsAndInvoicesInfo?.Customer?.Email || billsAndInvoicesInfo?.Customer?.Interac?.EmailAddress,
      Phone: billsAndInvoicesInfo?.Customer?.Phone || billsAndInvoicesInfo?.Customer?.Interac?.PhoneNumber || '',
      Question: billsAndInvoicesInfo?.Customer?.Interac?.Question,
      Answer: billsAndInvoicesInfo?.Customer?.Interac?.Answer,
      PhoneNumber: billsAndInvoicesInfo?.Customer?.BankAccount?.PhoneNumber,
      City: billsAndInvoicesInfo?.Customer?.BankAccount?.City,
      Address: billsAndInvoicesInfo?.Customer?.BankAccount?.Address,
      State: billsAndInvoicesInfo?.Customer?.BankAccount?.State,
      ZipCode: billsAndInvoicesInfo?.Customer?.BankAccount?.ZipCode,
      InteracMethod: billsAndInvoicesInfo?.Customer?.Interac?.EmailAddress
        ? 'Email Address'
        : billsAndInvoicesInfo?.Customer?.Interac?.PhoneNumber
          ? 'Phone Number'
          : 'Email Address',
      TokenDebit: billsAndInvoicesInfo?.Customer?.DebitCard?.Token,
      TokenCredit: billsAndInvoicesInfo?.Customer?.CreditCard?.Token,
      Language: billsAndInvoicesInfo?.Customer?.Interac?.Language || 'en',
    });
  }, [billsAndInvoicesInfo]);
  const [updateType, setUpdateType] = useState<'Add' | 'Edit'>('Edit');
  const onEdit = (paymentMethod: string) => {
    setUpdateType('Edit');
    setDefaultValues({
      ...defaultValues,
      PaymentMethod: paymentMethod,
    });
    setUpdateBankVisible(true);
  };
  const onAddNew = () => {
    setUpdateType('Add');
    setDefaultValues({
      ...defaultValues,
      PaymentMethod: '',
    });
    setUpdateBankVisible(true);
  };
  const paymentMethods = getAvailablePaymentMethodsDropdown(billsAndInvoicesInfo, Country, type, onEdit, tUtils);
  const totalAmount =
    Number(vp.sanitizeToNumeric(String(billsAndInvoices?.amountDue))) ||
    Number(vp.sanitizeToNumeric(String(billsAndInvoices?.totalAmount))) ||
    0;
  const paymentInfoRows = useBuildPaymentInfoRows({
    type,
    country: Country,
    maximumDate: billsAndInvoices?.dueDate || '',
    currency: billsAndInvoices?.currency || '',
    paymentMethods,
    paymentMethodsHeader: ({ close: closeDropdown }) =>
      getAvailablePaymentNew(billsAndInvoicesInfo, type, () => {
        onAddNew();
        closeDropdown();
      }, tUtils),
    totalAmount,
    creditNotes,
  });

  const [currentValues, setCurrentValues] = useState<PaymentInformation>({
    PaymentDate: '',
    PaymentAmount: '',
    PaymentMethod: '',
    CreditNotes: '',
    CreditAmount: 0,
  });
  const onSave = (values: PaymentInformation) => {
    if (billsAndInvoicesInfo) {
      defaultValues.PaymentMethod = values.PaymentMethod;
      onCollect(
        updateCustomerData(
          defaultValues,
          billsAndInvoicesInfo,
          Country,
          type,
          values.PaymentAmount || '',
          new Date(Number(values.PaymentDate)).toLocaleDateString('en-ca'),
          values.CreditNotes,
        ),
        isUpdated,
      );
    }
  };
  const paymentInformation = useMemo(
    () => ({
      PaymentDate: Date.now().toString(),
      PaymentAmount: billsAndInvoices?.PaidAmount || '0.00',
      PaymentMethod: billsAndInvoices?.paymentMethodUser || paymentMethods[0].value || '',
      CreditNotes: '',
      CreditAmount: 0,
    }),
    [billsAndInvoices, paymentMethods],
  );

  return (
    <Modal isVisible={isVisible} enableBgClick={false}>
      <div className={[styles.dialogue, updateBankVisible ? styles.disabledOverview : ''].join(' ')}>
        <BankingUpdateMiniDialogue
          isVisible={updateBankVisible}
          type={type}
          updateType={updateType}
          financialInstitutions={financialInstitutions}
          close={() => setUpdateBankVisible(false)}
          onSave={(values: PaymentUpdate) => {
            setDefaultValues(values);
            setUpdateBankVisible(false);
            setIsUpdated(true);
            if (billsAndInvoicesInfo) {
              const newValues = updateCustomerData(
                values,
                billsAndInvoicesInfo,
                Country,
                type,
                billsAndInvoicesInfo.PaidAmount || '',
                billsAndInvoicesInfo.scheduledDate || '',
              );
              setBillsAndInvoicesInfo(newValues);
            }
          }}
          originalValue={billsAndInvoicesInfo}
          defaultValues={defaultValues}
        />
        <div className={styles.dialogueContent}>
          <Formik
            initialValues={paymentInformation}
            onSubmit={(values, { resetForm }) => {
              onSave(values);
              resetForm();
            }}
            validationSchema={Yup.object().shape(getValidationSchema(currentValues, paymentInfoRows))}
            validateOnChange
            validateOnBlur
          >
            {({
              isValid,
              handleSubmit,
              resetForm,
              submitForm,
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setValues,
              setTouched,
              setFieldValue,
              validateForm,
            }) => {
              useEffect(() => {
                setCurrentValues({
                  ...values,
                });
              }, [values]);
              useEffect(() => {
                validateForm();
              }, [values, errors, paymentInfoRows]);
              useEffect(() => {
                setFieldValue('PaymentAmount', String(totalAmount - values.CreditAmount) || '');
              }, [values.PaymentDate, values.CreditNotes, isVisible]);
              useEffect(() => {
                if (isVisible) {
                  setFieldValue('PaymentMethod', billsAndInvoices?.paymentMethodUser || paymentMethods[0].value || '');
                } else {
                  setFieldValue('CreditNotes', '');
                  setFieldValue('CreditAmount', 0);
                }
              }, [isVisible]);
              useEffect(() => {
                if (billsAndInvoicesInfo?.paymentMethodUser !== values.PaymentMethod) {
                  setFieldValue(
                    'PaymentMethod',
                    billsAndInvoicesInfo?.paymentMethodUser || paymentMethods[0].value || '',
                  );
                }
              }, [billsAndInvoicesInfo?.paymentMethodUser]);

              return (
                <form onSubmit={handleSubmit} className={styles.dialogueForm}>
                  <Icon
                    className={styles.closeButton}
                    icon="cross"
                    height="12px"
                    width="12px"
                    onClick={() => {
                      resetForm();
                      validateForm();
                      close();
                    }}
                  />

                  <Box base="8" gap="lg" flx="col">
                    <div>
                      <SubTitle subTitle={type === 'Receivable' ? t('collectTitle') : t('sendTitle')} center />
                    </div>
                    <Box width="100%" gap="ml" base="8" flx="col">
                      {paymentInfoRows
                        .filter((element) => element.visible(values))
                        .map((element) =>
                          getFormElement(
                            element,
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            setValues,
                            setTouched,
                            setFieldValue,
                          ),
                        )}
                      <div className={styles.greyCard}>
                        <div className={styles.greyCardRemainingContainer}>
                          <div className={styles.greyCardLabel}>{t('remainingLabel')}</div>
                          <div className={styles.greyCardValue}>
                            {billsAndInvoices?.currency}{' '}
                            {vp.formattedCurrency(
                              String(
                                Math.round(
                                  totalAmount -
                                    Number(vp.sanitizeToNumeric(String(values.PaymentAmount))) -
                                    values.CreditAmount,
                                ) < //rounded due to floating point precision errors
                                  0
                                  ? vp.formattedCurrency(String(totalAmount))
                                  : vp.formattedCurrency(
                                      String(
                                        Math.abs(
                                          totalAmount -
                                            Number(vp.sanitizeToNumeric(String(values.PaymentAmount))) -
                                            values.CreditAmount,
                                        ), //due to floating point precision errors
                                      ),
                                    ),
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    </Box>

                    <div className={styles.dialogueTerm}>{t('footerInfo', { context: type })}</div>
                    <Box gap="sm" flx="row">
                      <Button
                        id="create-collection"
                        label={type === 'Receivable' ? t('collectButton') : t('payButton')}
                        color="primary"
                        onClick={submitForm}
                        isDisabled={!isValid}
                        width="100%"
                      />
                    </Box>
                  </Box>
                </form>
              );
            }}
          </Formik>
        </div>
      </div>
    </Modal>
  );
  function getFormElement(
    element: PaymentInfoRows,
    values: PaymentInformation,
    errors: FormikErrors<PaymentInformation>,
    touched: FormikTouched<PaymentInformation>,
    handleChange: FormikHandlers['handleChange'],
    handleBlur: FormikHandlers['handleBlur'],
    setValues: (values: React.SetStateAction<PaymentInformation>, shouldValidate?: boolean | undefined) => void,
    setTouched: (touched: FormikTouched<PaymentInformation>, shouldValidate?: boolean | undefined) => void,
    setFieldValue: FormikHelpers<PaymentInformation>['setFieldValue'],
  ) {
    const {
      type: elementType,
      id,
      preset,
      placeholder,
      label,
      require: required,
      getSelectOptions,
      getMultiSelectOptions,
      container,
      paymentRows,
      min,
      getElementLeft,
      getElementRight,
      disabled,
      renderOption,
      renderSelectedValue,
      optionVisibility,
      renderDropdownHeader,
      disabledMessage,
    } = element;
    if (container === 'inputs') {
      const isRequired = required(values);
      return (
        <Box flx="col" width="100%">
          <ElementContainer
            width="100%"
            padding="0 0 0"
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            isBold={false}
            input={
              <Box flx="col" width="100%">
                {paymentRows
                  .filter((subElement) => subElement.visible(values))
                  .map((subElement) =>
                    getFormElement(
                      subElement,
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      setValues,
                      setTouched,
                      setFieldValue,
                    ),
                  )}
              </Box>
            }
          />
        </Box>
      );
    }
    if (container === 'wrapper' || id === undefined) {
      return (
        <Box gap="sm" ai="fs" width="100%">
          {paymentRows
            .filter((subElement) => subElement.visible(values))
            .map((subElement) =>
              getFormElement(
                subElement,
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                setValues,
                setTouched,
                setFieldValue,
              ),
            )}
        </Box>
      );
    }

    const isRequired = required(values);

    switch (elementType) {
      case 'input':
        return (
          <ElementContainer
            width="100%"
            padding="0 0 6px"
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            isBold={false}
            input={
              <Box flx="col" gapSize="6px">
                <TextField
                  variant="outlined"
                  preset={preset}
                  name={id}
                  placeholder={placeholder}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  inputWidth="calc(100% - 172px)"
                  height="37px"
                  value={String(values[id] || '')}
                  disabled={disabled ? disabled(values) : false}
                  elementLeft={getElementLeft ? getElementLeft(values) : undefined}
                  elementRight={getElementRight ? getElementRight(values) : undefined}
                  error={errors[id] && touched[id] ? errors[id] : null}
                />
                {disabled && disabled(values) && disabledMessage && (
                  <div className={styles.disabledMessage}>{disabledMessage}</div>
                )}
              </Box>
            }
          />
        );
      case 'select':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            justifyContent="flex-start"
            isBold={false}
            padding="0 0 6px"
            input={
              <Select
                version="2"
                options={
                  getSelectOptions
                    ? getSelectOptions(values).filter((option) => (optionVisibility ? optionVisibility(option) : true))
                    : []
                }
                renderOption={renderOption}
                renderSelectedValue={renderSelectedValue}
                renderDropdownHeader={renderDropdownHeader}
                optionValue={(option) => option.value}
                placeholder={placeholder || ''}
                name={id}
                onBlur={handleBlur}
                onChange={handleChange}
                style={{
                  '--triggerFontSize': '14px',
                  '--triggerBorderRadius': '4px',
                }}
                value={String(values[id] || '')}
                errorMsg={errors[id] && touched[id] ? errors[id] : undefined}
              />
            }
          />
        );
      case 'multiSelect':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            justifyContent="flex-start"
            isBold={false}
            padding="0 0 6px"
            input={
              <FilterableMultiSelector
                variant="outline"
                isNew={false}
                items={getMultiSelectOptions ? (getMultiSelectOptions(values) as vp.SelectOption[]) : []}
                placeholder={placeholder || ''}
                disabledLabel={placeholder || ''}
                name={id}
                value={creditNotes
                  .map(
                    (creditNote) =>
                      ({
                        label: `${creditNote.currency} ${vp.formattedCurrency(String(creditNote.remainingCredit))}`,
                        value: creditNote.id,
                      }) as vp.SelectOption<Record<string, never>>,
                  )
                  .filter((item) => {
                    return values[id].split(',').includes(item.value);
                  })}
                error={errors[id] && touched[id] ? errors[id] : null}
                disabled={disabled ? disabled(values) : false}
                isDisabled={disabled ? disabled(values) : false}
                nosort
                onSelect={(selected) => {
                  setFieldValue(id, selected.map((item) => item.value).join(','));
                  setFieldValue(
                    'CreditAmount',
                    creditNotes
                      .filter((item) => selected.map((itemMap) => itemMap.value).includes(item.id))
                      .reduce((acc, item) => acc + item.remainingCredit, 0),
                  );
                }}
              />
            }
          />
        );

      case 'date':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            padding="0 0 6px"
            isBold={false}
            justifyContent="flex-start"
            input={
              <DatepickerSimple
                variant="outline"
                name={id}
                value={String(values[id] || '')}
                onBlur={handleBlur}
                onChange={handleChange}
                error={errors[id] ? errors[id] : null}
                min={vp.createTimestamp(min)}
              />
            }
          />
        );
      default:
        return <></>;
    }
  }

  function getValidationSchema(
    ctx: PaymentInformation,
    rows: PaymentInfoRows[],
    initialValidationSchema?: { [key: string]: any },
  ): { [key: string]: any } {
    const validations = rows.reduce(
      (existingFields, row) =>
        row.id && (row.container === 'field' || row.container === 'inputs')
          ? {
              ...existingFields,
              [row.id]: row.validate ? row.validate(ctx) : undefined,
            }
          : { ...existingFields, ...getValidationSchema(ctx, row.paymentRows) },
      { ...initialValidationSchema },
    );
    return validations;
  }
};

export default AccountingSystemSingleDialogue;
